/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const AUTO_CREATE_GHOST_DISCRIMINATOR = new Uint8Array([
  50, 170, 232, 73, 238, 200, 180, 16,
]);

export function getAutoCreateGhostDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    AUTO_CREATE_GHOST_DISCRIMINATOR,
  );
}

export type AutoCreateGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgentAccount extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgentAccount extends string
        ? WritableAccount<TAccountAgentAccount>
        : TAccountAgentAccount,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type AutoCreateGhostInstructionData = {
  discriminator: ReadonlyUint8Array;
  paymentAddress: Address;
  firstTxSignature: string;
  discoverySource: string;
  initialGhostScore: Option<bigint>;
};

export type AutoCreateGhostInstructionDataArgs = {
  paymentAddress: Address;
  firstTxSignature: string;
  discoverySource: string;
  initialGhostScore: OptionOrNullable<number | bigint>;
};

export function getAutoCreateGhostInstructionDataEncoder(): Encoder<AutoCreateGhostInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["paymentAddress", getAddressEncoder()],
      [
        "firstTxSignature",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      [
        "discoverySource",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["initialGhostScore", getOptionEncoder(getU64Encoder())],
    ]),
    (value) => ({ ...value, discriminator: AUTO_CREATE_GHOST_DISCRIMINATOR }),
  );
}

export function getAutoCreateGhostInstructionDataDecoder(): Decoder<AutoCreateGhostInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["paymentAddress", getAddressDecoder()],
    [
      "firstTxSignature",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    [
      "discoverySource",
      addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder()),
    ],
    ["initialGhostScore", getOptionDecoder(getU64Decoder())],
  ]);
}

export function getAutoCreateGhostInstructionDataCodec(): Codec<
  AutoCreateGhostInstructionDataArgs,
  AutoCreateGhostInstructionData
> {
  return combineCodec(
    getAutoCreateGhostInstructionDataEncoder(),
    getAutoCreateGhostInstructionDataDecoder(),
  );
}

export type AutoCreateGhostAsyncInput<
  TAccountAgentAccount extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  agentAccount?: Address<TAccountAgentAccount>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  paymentAddress: AutoCreateGhostInstructionDataArgs["paymentAddress"];
  firstTxSignature: AutoCreateGhostInstructionDataArgs["firstTxSignature"];
  discoverySource: AutoCreateGhostInstructionDataArgs["discoverySource"];
  initialGhostScore: AutoCreateGhostInstructionDataArgs["initialGhostScore"];
};

export async function getAutoCreateGhostInstructionAsync<
  TAccountAgentAccount extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: AutoCreateGhostAsyncInput<
    TAccountAgentAccount,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  AutoCreateGhostInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountAuthority,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.agentAccount.value) {
    accounts.agentAccount.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([97, 103, 101, 110, 116])),
        getAddressEncoder().encode(expectSome(args.paymentAddress)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getAutoCreateGhostInstructionDataEncoder().encode(
      args as AutoCreateGhostInstructionDataArgs,
    ),
    programAddress,
  } as AutoCreateGhostInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type AutoCreateGhostInput<
  TAccountAgentAccount extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  agentAccount: Address<TAccountAgentAccount>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  paymentAddress: AutoCreateGhostInstructionDataArgs["paymentAddress"];
  firstTxSignature: AutoCreateGhostInstructionDataArgs["firstTxSignature"];
  discoverySource: AutoCreateGhostInstructionDataArgs["discoverySource"];
  initialGhostScore: AutoCreateGhostInstructionDataArgs["initialGhostScore"];
};

export function getAutoCreateGhostInstruction<
  TAccountAgentAccount extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: AutoCreateGhostInput<
    TAccountAgentAccount,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): AutoCreateGhostInstruction<
  TProgramAddress,
  TAccountAgentAccount,
  TAccountAuthority,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getAutoCreateGhostInstructionDataEncoder().encode(
      args as AutoCreateGhostInstructionDataArgs,
    ),
    programAddress,
  } as AutoCreateGhostInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type ParsedAutoCreateGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    agentAccount: TAccountMetas[0];
    authority: TAccountMetas[1];
    systemProgram: TAccountMetas[2];
  };
  data: AutoCreateGhostInstructionData;
};

export function parseAutoCreateGhostInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedAutoCreateGhostInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agentAccount: getNextAccount(),
      authority: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getAutoCreateGhostInstructionDataDecoder().decode(instruction.data),
  };
}
